import { API_BASE_URL } from '../constants';
import { getErrorMessage } from '../utils';
import {
  toCamelCase,
  toSnakeCase,
  transformFromAPI,
  transformPurchaseOrderForAPI,
  transformPurchaseOrderFromAPI
} from '../utils/dataTransform';
import apiService from './api';

class PurchaseOrderService {
  constructor() {
    this.baseURL = '/purchase-orders';
  }

  async getPurchaseOrders(params = {}) {
    try {
      // Convert frontend pagination params to backend format
      const apiParams = {
        page: params.page || 1,
        limit: params.limit || 25,
        ...params
      };

      // Remove frontend-specific pagination params that don't match backend
      delete apiParams.offset;
      delete apiParams.per_page;

      const response = await apiService.get(this.baseURL, apiParams);

      if (response.success) {
        // Transform snake_case response to camelCase for frontend
        const transformedData = {
          ...response,
          data: response.data ? response.data.map(po => transformPurchaseOrderFromAPI(po)) : []
        };
        return transformedData;
      } else {
        throw new Error(response.message || 'Failed to fetch purchase orders');
      }
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      throw new Error(getErrorMessage(error, 'Failed to fetch purchase orders'));
    }
  }

  async getPurchaseOrderById(id) {
    try {
      const response = await apiService.get(`${this.baseURL}/${id}`);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformPurchaseOrderFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to fetch purchase order');
      }
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to fetch purchase order'));
    }
  }

  async createPurchaseOrder(poData) {
    try {
      // Transform camelCase form data to snake_case for API
      const apiData = transformPurchaseOrderForAPI(poData);

      const response = await apiService.post(this.baseURL, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformPurchaseOrderFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to create purchase order');
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to create purchase order'));
    }
  }

  async createPurchaseOrderWithDetails(poData) {
    try {
      // Transform camelCase form data to snake_case for API
      const apiData = transformPurchaseOrderForAPI(poData);

      const response = await apiService.post(`${this.baseURL}/with-details`, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformPurchaseOrderFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to create purchase order with details');
      }
    } catch (error) {
      console.error('Error creating purchase order with details:', error);
      throw new Error(getErrorMessage(error, 'Failed to create purchase order with details'));
    }
  }

  async updatePurchaseOrder(id, poData) {
    try {
      console.log('updatePurchaseOrder called with ID:', id, 'data:', poData);
      // Transform camelCase to snake_case for API (update only sends partial fields)
      const apiData = toSnakeCase(poData);
      console.log('updatePurchaseOrder transformed data:', apiData);

      const response = await apiService.put(`${this.baseURL}/${id}`, apiData);
      console.log('updatePurchaseOrder response:', response);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformPurchaseOrderFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to update purchase order');
      }
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to update purchase order'));
    }
  }

  async deletePurchaseOrder(id) {
    try {
      const response = await apiService.delete(`${this.baseURL}/${id}`);

      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete purchase order');
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to delete purchase order'));
    }
  }

  async approvePurchaseOrder(id, comments = '') {
    try {
      const response = await apiService.put(`${this.baseURL}/${id}/approve`, { comments });

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to approve purchase order');
      }
    } catch (error) {
      console.error('Error approving purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to approve purchase order'));
    }
  }

  async rejectPurchaseOrder(id, comments = '') {
    try {
      const response = await apiService.put(`${this.baseURL}/${id}/reject`, { comments });

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to reject purchase order');
      }
    } catch (error) {
      console.error('Error rejecting purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to reject purchase order'));
    }
  }

  async applyPurchaseOrder(id, comments = '') {
    try {
      const response = await apiService.put(`${this.baseURL}/${id}/apply`, { comments });

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to submit purchase order for approval');
      }
    } catch (error) {
      console.error('Error submitting purchase order for approval:', error);
      throw new Error(getErrorMessage(error, 'Failed to submit purchase order for approval'));
    }
  }

  async cancelPurchaseOrder(id, reason = '', comments = '') {
    try {
      const response = await apiService.put(`${this.baseURL}/${id}/cancel`, {
        reason,
        comments
      });

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to cancel purchase order');
      }
    } catch (error) {
      console.error('Error cancelling purchase order:', error);
      throw new Error(getErrorMessage(error, 'Failed to cancel purchase order'));
    }
  }

  async getPurchaseOrderStatusHistory(poId) {
    try {
      const response = await apiService.get(`/purchase-order-status-history/po/${poId}`);

      if (response.success) {
        return {
          ...response,
          data: response.data ? response.data.map(history => transformFromAPI(history)) : []
        };
      } else {
        throw new Error(response.message || 'Failed to fetch purchase order status history');
      }
    } catch (error) {
      console.error('Error fetching purchase order status history:', error);
      throw new Error(getErrorMessage(error, 'Failed to fetch purchase order status history'));
    }
  }

  // Legacy method - use getPurchaseOrderStatusHistory instead
  async getPOHistory(id) {
    return this.getPurchaseOrderStatusHistory(id);
  }

  async downloadPOPDF(poId) {
    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`${API_BASE_URL}${this.baseURL}/${poId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      return response.blob();
    } catch (error) {
      console.error('Error downloading PO PDF:', error);
      throw new Error(getErrorMessage(error, 'Failed to download purchase order PDF'));
    }
  }

  async getPurchaseOrderItems(poId) {
    try {
      const response = await apiService.get(`/purchase-order-items/po/${poId}`);

      if (response.success) {
        const data = response.data ? toCamelCase(response.data) : [];
        return { ...response, data };
      }

      throw new Error(response.message || 'Failed to fetch purchase order items');
    } catch (error) {
      console.error('Error fetching purchase order items:', error);
      throw new Error(getErrorMessage(error, 'Failed to fetch purchase order items'));
    }
  }

  async getPurchaseOrderCalculations(poId) {
    try {
      const response = await apiService.get(`/purchase-order-calculations/po/${poId}`);

      if (response.success) {
        const data = response.data ? toCamelCase(response.data) : null;
        return { ...response, data };
      }

      throw new Error(response.message || 'Failed to fetch purchase order calculations');
    } catch (error) {
      console.error('Error fetching purchase order calculations:', error);
      throw new Error(getErrorMessage(error, 'Failed to fetch purchase order calculations'));
    }
  }

  async updatePurchaseOrderItem(itemId, itemData) {
    try {
      // Transform camelCase to snake_case for API (update only sends partial fields)
      const apiData = toSnakeCase(itemData);
      const response = await apiService.put(`/purchase-order-items/${itemId}`, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? toCamelCase(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to update purchase order item');
      }
    } catch (error) {
      console.error('Error updating purchase order item:', error);
      throw new Error(getErrorMessage(error, 'Failed to update purchase order item'));
    }
  }

  async createPurchaseOrderItem(poId, itemData) {
    try {
      // Transform camelCase to snake_case and add po_id
      const apiData = toSnakeCase({ ...itemData, poId });
      const response = await apiService.post('/purchase-order-items', apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? toCamelCase(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to create purchase order item');
      }
    } catch (error) {
      console.error('Error creating purchase order item:', error);
      throw new Error(getErrorMessage(error, 'Failed to create purchase order item'));
    }
  }

  async deletePurchaseOrderItem(itemId) {
    try {
      const response = await apiService.delete(`/purchase-order-items/${itemId}`);

      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete purchase order item');
      }
    } catch (error) {
      console.error('Error deleting purchase order item:', error);
      throw new Error(getErrorMessage(error, 'Failed to delete purchase order item'));
    }
  }

  async updatePurchaseOrderCalculation(calcId, calcData) {
    try {
      // Ensure all numeric calculation fields are properly converted to numbers
      const numericFields = ['subTotal', 'roundOff', 'gstAmount', 'totalAmount'];
      const sanitizedCalcData = { ...calcData };

      numericFields.forEach(field => {
        if (sanitizedCalcData[field] !== undefined && sanitizedCalcData[field] !== null) {
          const numValue = typeof sanitizedCalcData[field] === 'string'
            ? parseFloat(sanitizedCalcData[field])
            : Number(sanitizedCalcData[field]);

          // Validate the parsed number
          if (isNaN(numValue)) {
            console.warn(`Invalid numeric value for ${field}:`, sanitizedCalcData[field]);
            sanitizedCalcData[field] = 0;
          } else {
            sanitizedCalcData[field] = numValue;
          }
        }
      });

      // Transform camelCase to snake_case for API (update only sends partial fields)
      const apiData = toSnakeCase(sanitizedCalcData);
      const response = await apiService.put(`/purchase-order-calculations/${calcId}`, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? toCamelCase(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to update purchase order calculation');
      }
    } catch (error) {
      console.error('Error updating purchase order calculation:', error);
      throw new Error(getErrorMessage(error, 'Failed to update purchase order calculation'));
    }
  }

  async sendToSupplier(id) {
    try {
      const response = await apiService.put(`${this.baseURL}/${id}/send-to-supplier`);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to send purchase order to supplier');
      }
    } catch (error) {
      console.error('Error sending purchase order to supplier:', error);
      throw new Error(getErrorMessage(error, 'Failed to send purchase order to supplier'));
    }
  }

  async recordPhoneResponse(id, responseData) {
    try {
      const apiData = toSnakeCase(responseData);
      const response = await apiService.put(`${this.baseURL}/${id}/record-phone-response`, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to record phone response');
      }
    } catch (error) {
      console.error('Error recording phone response:', error);
      throw new Error(getErrorMessage(error, 'Failed to record phone response'));
    }
  }

  async editSupplierResponse(id, responseData) {
    try {
      const apiData = toSnakeCase(responseData);
      const response = await apiService.put(`${this.baseURL}/${id}/edit-supplier-response`, apiData);

      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to edit supplier response');
      }
    } catch (error) {
      console.error('Error editing supplier response:', error);
      throw new Error(getErrorMessage(error, 'Failed to edit supplier response'));
    }
  }
}

export const purchaseOrderService = new PurchaseOrderService();

